import { SEARCH_PARAM_SITE_ID } from 'routes/(global_search_params)/-searchParams'
import { documentsSearchSchema } from 'routes/documents/-searchParams'
import { z } from 'zod'

export const modelOperationsSectionSchema = z.enum([
  'source-lists',
  'evaluation',
  'dossiers',
  'fusion-runs',
  'time-series-linkage',
  'asset-templates',
  'fusion-output',
  'sentinels',
  'create-sentinel-from-docs',
  'value-stories',
])

export type ModelOperationsSection = z.infer<
  typeof modelOperationsSectionSchema
>

/**
 * Model Operations is a single workspace destination; sections and selections
 * are in-page state on this one route so the singleton tab can restore them.
 */
/**
 * The sentinel sections embed the knowledge document viewer, whose URL-sync
 * (`useTabState`) reads/writes the document viewer params via
 * `useSearch({ from: '/model-operations/' })`, so the full documents schema
 * is spread in to make those params valid on this route.
 */
export const modelOperationsSearchParams = z.object({
  ...documentsSearchSchema.shape,
  [SEARCH_PARAM_SITE_ID]: z.string().optional(),
  section: modelOperationsSectionSchema.optional().catch(undefined),
  /** Selected source list entry id. */
  list: z.string().optional(),
  /** Selected source record id within the selected list. */
  recordId: z.string().optional(),
  /** Selected PI tag dossier entry id (`fim-claim-<uuid>`). */
  dossierId: z.string().optional(),
  /** One-shot seed for the PI Tag Dossiers list search. */
  dossierSearch: z.string().optional(),
  /** Selected fusion run id. */
  runId: z.string().optional(),
  /** Selected time-series-linkage run id. */
  matchRunId: z.string().optional(),
  /** Selected asset-template entry id (`fim-asset-template-<template_id>`). */
  templateId: z.string().optional(),
  /** Selected sentinel entry id (Sentinels section). */
  sentinelId: z.string().optional(),
  /** In-progress sentinel design context id, so the create flow survives a
   * reload. */
  designContextId: z.string().optional(),
  /** Selected conversation id (Value Stories section). */
  conversationId: z.string().optional(),
  /** Ground-truth source list id (Evaluation section). */
  evalGtList: z.string().optional(),
  /** Candidate source list id scored against the ground truth. */
  evalCandidateList: z.string().optional(),
})

export type ModelOperationsSearchParams = z.infer<
  typeof modelOperationsSearchParams
>

export const createModelOperationsSearch = (siteId: string | undefined) => ({
  [SEARCH_PARAM_SITE_ID]: siteId || undefined,
})
